import { useMemo, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { MdOutlineFileDownload, MdArrowDownward, MdArrowUpward } from 'react-icons/md';
import { format, parseISO } from 'date-fns';
import {
  AlexaVisibilityResult,
  AlexaVisibilityRun
} from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { useSetError } from '@src/redux/modals';
import { download, downloadJSONToCSV, exportMultiSheetToExcel } from '@src/utils/download-utils';
import {
  buildAllDataExport,
  fetchResultsInWindows,
  getAllDataFileName,
  getHistoryWindow
} from '../alexa-visibility-export-utils';
import { buildResultsExportRows, buildRunsExportRows } from '../alexa-visibility-utils';
import { fetchAlexaVisibilityResults } from '../hooks/useAlexaVisibilityResults';

interface AllResultsTabProps {
  results: AlexaVisibilityResult[];
  runs: AlexaVisibilityRun[];
  // Every run for the brand, unaffected by the date filter, for the all-data export.
  allRuns: AlexaVisibilityRun[];
  targetBrand: string;
}

interface ResultRow {
  run: string;
  query: string;
  category: string;
  persona: string;
  targetBrandMentioned: string;
  targetBrandRank: string;
  citationStrength: string;
  responseFormat: string;
  status: string;
  createdAt: string;
}

// A failed/timed-out query has no classification -- surface that explicitly,
// rather than letting the row render as a near-empty line with no explanation.
const toStatus = (result: AlexaVisibilityResult): string => {
  if (result.error) return result.error;
  if (result.timedOut) return 'Timed out';
  if (result.possiblyTruncated) return 'Possibly truncated';
  return 'OK';
};

const toRow = (result: AlexaVisibilityResult, runNumberById: Map<string, number>): ResultRow => ({
  run: String(runNumberById.get(result.runId) ?? ''),
  query: result.query,
  category: result.category ?? '',
  persona: result.persona ?? '',
  targetBrandMentioned: result.classification?.targetBrandMentioned ? 'Yes' : 'No',
  targetBrandRank:
    typeof result.classification?.targetBrandRank === 'number'
      ? String(result.classification.targetBrandRank)
      : '',
  citationStrength: result.classification?.citationStrength ?? '',
  responseFormat: result.classification?.responseFormat ?? '',
  status: toStatus(result),
  createdAt: format(parseISO(result.createdAt), 'yyyy-MM-dd HH:mm')
});

const COLUMNS: { key: keyof ResultRow; label: string }[] = [
  { key: 'run', label: 'Run #' },
  { key: 'query', label: 'Query' },
  { key: 'category', label: 'Category' },
  { key: 'persona', label: 'Persona' },
  { key: 'targetBrandMentioned', label: 'Mentioned' },
  { key: 'targetBrandRank', label: 'Rank' },
  { key: 'citationStrength', label: 'Citation Strength' },
  { key: 'responseFormat', label: 'Response Format' },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Created At' }
];

export const AllResultsTab = ({ results, runs, allRuns, targetBrand }: AllResultsTabProps) => {
  const [searchText, setSearchText] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof ResultRow>('createdAt');
  const [sortAscending, setSortAscending] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const company = useAppSelector(currentCompanySelector);
  const setError = useSetError();

  const rows = useMemo(() => {
    const runNumberById = new Map(runs.map(run => [run.id, run.runNumber]));
    return results.map(result => toRow(result, runNumberById));
  }, [results, runs]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const searched = normalizedSearch
      ? rows.filter(
          row =>
            row.query.toLowerCase().includes(normalizedSearch) ||
            row.category.toLowerCase().includes(normalizedSearch) ||
            row.persona.toLowerCase().includes(normalizedSearch)
        )
      : rows;

    return [...searched].sort((a, b) => {
      const valueA = a[sortColumn];
      const valueB = b[sortColumn];
      const comparison = valueA.localeCompare(valueB, undefined, { numeric: true });
      return sortAscending ? comparison : -comparison;
    });
  }, [rows, searchText, sortColumn, sortAscending]);

  const handleSort = (column: keyof ResultRow) => {
    if (column === sortColumn) {
      setSortAscending(!sortAscending);
    } else {
      setSortColumn(column);
      setSortAscending(true);
    }
  };

  const handleDownloadCsv = () => {
    downloadJSONToCSV(filteredRows, `AlexaVisibility_AllResults_${targetBrand}`);
  };

  // Always exports every scan for the brand, regardless of the search filter.
  const handleDownloadExcel = async () => {
    setIsExporting(true);
    try {
      await exportMultiSheetToExcel(
        [
          { name: 'Runs', data: buildRunsExportRows(runs) },
          { name: 'Results', data: buildResultsExportRows(results, runs) }
        ],
        `AlexaVisibility_ScanHistory_${targetBrand}`
      );
    } catch (e) {
      setError({
        title: "Couldn't export scans",
        message: `Failed to export Alexa Visibility scans to Excel: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Everything collected for the brand, for every run: not just what is on screen. A request returns
  // at most the newest 1,000 results, so the whole history is fetched in date windows.
  const handleExportAllData = async () => {
    setIsExportingAll(true);
    try {
      const { startMs, endMs } = getHistoryWindow(allRuns);
      const { results: everything, complete } = await fetchResultsInWindows(
        (dateFrom, dateTo) =>
          fetchAlexaVisibilityResults({ company, brand: targetBrand, dateFrom, dateTo }),
        startMs,
        endMs
      );
      const payload = buildAllDataExport({
        company,
        brand: targetBrand,
        runs: allRuns,
        results: everything,
        complete
      });
      download(JSON.stringify(payload), getAllDataFileName(targetBrand), 'application/json');
      if (!complete) {
        setError({
          title: 'The export may be incomplete',
          message:
            'Some results could not be fetched, so the file may be missing a few. The file says so in its "about.complete" field.'
        });
      }
    } catch (e) {
      setError({
        title: "Couldn't export all data",
        message: `Failed to export all Alexa Visibility data: ${e.message}`,
        reportError: e
      });
    } finally {
      setIsExportingAll(false);
    }
  };

  if (results.length === 0) {
    return (
      <EmptyState
        title='No results yet'
        subtitle='Every scraped query will show up here as a sortable, filterable row.'
      />
    );
  }

  return (
    <div className='all-results-tab'>
      <div className='aars-tab-actions'>
        <Form.Control
          type='search'
          placeholder='Search query, category, or persona'
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <div className='aars-export-buttons'>
          <Button
            size={Size.SM}
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleExportAllData}
            loading={isExportingAll}
          >
            Export All Data (JSON)
          </Button>
          <Button
            size={Size.SM}
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleDownloadCsv}
          >
            Export CSV
          </Button>
          <Button
            size={Size.SM}
            leadingElement={<MdOutlineFileDownload />}
            onClick={handleDownloadExcel}
            loading={isExporting}
          >
            Export All Scans (Excel)
          </Button>
        </div>
      </div>
      <div className='aars-table-scroll'>
        <Table className='all-results-table' hover>
          <thead>
            <tr>
              {COLUMNS.map(column => (
                <th key={column.key}>
                  <InteractiveDiv
                    className='aars-sortable-header'
                    onClick={() => handleSort(column.key)}
                  >
                    {column.label}
                    {sortColumn === column.key &&
                      (sortAscending ? <MdArrowUpward /> : <MdArrowDownward />)}
                  </InteractiveDiv>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map(row => (
              <tr key={`${row.query}-${row.createdAt}`}>
                {COLUMNS.map(column => (
                  <td key={column.key}>{row[column.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};
